import { useState } from "react";
import { Box, type StackProps, IconButton, Flex, Stack } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { type Currency } from "@looksrare/config";
import { ArrowLeftIcon, EthTokenIcon, Text, TokenYoloIcon, getStepStatus } from "@looksrare/uikit";
import type { BigIntish } from "@looksrare/utils";
import { InputPanelView } from "../types";
import { FlipperGameStore } from "../hooks/useFlipper";
import { ApproveTransferManagerStep, ApproveYoloStep, PlayStep, RefundStep } from "./TransactionSteps";

interface PlayTransactionViewProps extends StackProps {
  currentCurrency: Currency;
  numberOfRounds: string;
  totalWagerEth: number;
  maxPayout: number;
  totalWagerYoloWei: BigIntish;
  isCancelled: boolean;
  flipCoin: FlipperGameStore["flipCoin"];
  claimRefund: FlipperGameStore["claimRefund"];
  setView: (view: InputPanelView) => void;
  onFlipComplete: () => void;
  onFlipError: () => void;
  setFlipperStep: FlipperGameStore["setFlipperStep"];
}

enum PlayTransactionStep {
  ClaimRefund,
  ApproveTransferManager,
  ApproveERC20,
  Play,
}

export const PlayTransactionView = ({
  currentCurrency,
  numberOfRounds,
  totalWagerEth,
  totalWagerYoloWei,
  maxPayout,
  isCancelled,
  setView,
  flipCoin,
  claimRefund,
  onFlipComplete,
  onFlipError,
  setFlipperStep,
  ...props
}: PlayTransactionViewProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(() => {
    if (currentCurrency !== "ETH") {
      return PlayTransactionStep.ApproveTransferManager;
    } else if (isCancelled) {
      return PlayTransactionStep.ClaimRefund;
    }
    return PlayTransactionStep.Play;
  });
  const TokenIcon = currentCurrency === "ETH" ? EthTokenIcon : TokenYoloIcon;

  return (
    <Stack
      width={{ base: "100%", md: "320px" }}
      flexShrink={0}
      spacing={0}
      pt={0}
      borderRadius="dialog"
      bg="rgba(255, 255, 255, 0.09)"
      alignSelf="flex-start"
      key="confirmEntry"
      {...props}
    >
      {/* Header */}
      <Flex p={4} gap={4} width="100%" alignItems="center">
        <IconButton
          aria-label="back"
          size="sm"
          variant="ghost"
          colorScheme="secondary"
          onClick={() => setView(InputPanelView.Default)}
        >
          <ArrowLeftIcon />
        </IconButton>
        <Text bold>{t("flipper::Confirm Entry")}</Text>
      </Flex>
      {/* Body */}
      <Stack px={4} pt={6} pb={8} spacing={1}>
        {[
          { text: t("flipper::Total Flips"), value: numberOfRounds, isValueCurrency: false },
          { text: t("flipper::Total Entry"), value: totalWagerEth, isValueCurrency: true },
          { text: t("flipper::Max Payout"), value: maxPayout, isValueCurrency: true },
        ].map(({ text, value, isValueCurrency }) => (
          <Flex key={text} gap={1} alignItems="flex-end">
            <Text bold>{text}</Text>
            <Box
              flexGrow={1}
              alignSelf="flex-end"
              borderBottomWidth="1px"
              borderStyle="dotted"
              borderColor="border-01"
            />
            <Flex alignItems="center">
              <Text
                textStyle="detail"
                bold
                // adjust mr to line up with the TokenIcon
                mr={1}
              >
                {value}
              </Text>
              {isValueCurrency && <TokenIcon boxSize={4} />}
            </Flex>
          </Flex>
        ))}
        <Text mt={3} textStyle="detail">
          {t("flipper::Confirm your entry in your wallet app.")}
        </Text>
      </Stack>
      <Stack px={4} pb={4}>
        {isCancelled && (
          <>
            <RefundStep
              status={getStepStatus(PlayTransactionStep.ClaimRefund, step)}
              collapse={step !== PlayTransactionStep.ClaimRefund}
              isCurrentStep={step === PlayTransactionStep.ClaimRefund}
              onError={onFlipError}
              claimRefund={claimRefund}
              onComplete={() =>
                setStep(currentCurrency === "YOLO" ? PlayTransactionStep.ApproveERC20 : PlayTransactionStep.Play)
              }
              width="100%"
            />
          </>
        )}
        {currentCurrency === "YOLO" && (
          <>
            <ApproveTransferManagerStep
              status={getStepStatus(PlayTransactionStep.ApproveTransferManager, step)}
              collapse={step !== PlayTransactionStep.ApproveTransferManager}
              isCurrentStep={step === PlayTransactionStep.ApproveTransferManager}
              onComplete={() => setStep(PlayTransactionStep.ApproveERC20)}
              width="100%"
            />
            <ApproveYoloStep
              status={getStepStatus(PlayTransactionStep.ApproveERC20, step)}
              collapse={step !== PlayTransactionStep.ApproveERC20}
              isCurrentStep={step === PlayTransactionStep.ApproveERC20}
              onComplete={() => setStep(PlayTransactionStep.Play)}
              width="100%"
              amountWei={totalWagerYoloWei}
            />
          </>
        )}
        <PlayStep
          status={getStepStatus(PlayTransactionStep.Play, step)}
          collapse={step !== PlayTransactionStep.Play}
          isCurrentStep={step === PlayTransactionStep.Play}
          onComplete={onFlipComplete}
          onError={onFlipError}
          flipCoin={flipCoin}
          width="100%"
          setFlipperStep={setFlipperStep}
        />
      </Stack>
    </Stack>
  );
};
